import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Wrench, X } from 'lucide-react';
import {
  Field,
  TextInput,
  TextareaInput,
  FormSection,
  PrimaryButton,
  GhostButton,
} from '../ui/Field';
import type { ToolModel } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// ToolModelForm — Operations Deck modal for the Tool Models
// catalogue. Mirrors CompanyForm / SiteForm / UserForm / ToolForm:
//   • Sticky header — icon tile · breadcrumb · Fraunces title · close
//   • Error summary banner when submit fails validation
//   • Scrollable body — 01 Identity (name + mono code) + 02 Description
//   • Sticky footer — last-update caption · Cancel / Save
//
// All inputs use the shared Field primitives so typography is
// identical to the other forms in the app.
// ─────────────────────────────────────────────────────────────

const emptyModel: Partial<ToolModel> = {
  toolModelName: '',
  model: '',
  description: '',
};

export interface ToolModelErrors {
  toolModelName?: string;
  model?: string;
  description?: string;
}

function validate(form: Partial<ToolModel>): ToolModelErrors {
  const e: ToolModelErrors = {};
  if (!form.toolModelName?.trim()) e.toolModelName = 'Name is required';
  if (!form.model?.trim()) e.model = 'Model code is required';
  if (!form.description?.trim()) e.description = 'Description is required';
  return e;
}

export interface ToolModelFormProps {
  open: boolean;
  editing: ToolModel | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (form: Partial<ToolModel>) => Promise<void> | void;
  /**
   * Server-returned save error mapped to a specific field — useful for
   * surfacing "Model number already exists" right under the field.
   */
  serverFieldError?: { field: keyof ToolModelErrors; message: string } | null;
}

export function ToolModelForm({
  open,
  editing,
  saving,
  onCancel,
  onSave,
  serverFieldError,
}: ToolModelFormProps) {
  const [form, setForm] = useState<Partial<ToolModel>>(emptyModel);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<ToolModelErrors>({});

  // Re-seed whenever the modal opens with a fresh editing target.
  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setErrors({});
    setForm(editing ? { ...editing } : { ...emptyModel });
  }, [open, editing]);

  // Lock body scroll while modal is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Escape closes (unless mid-save).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, saving, onCancel]);

  // Server validation errors surface under the matching field.
  useEffect(() => {
    if (!serverFieldError) return;
    setErrors((prev) => ({ ...prev, [serverFieldError.field]: serverFieldError.message }));
    setSubmitted(true);
  }, [serverFieldError]);

  if (!open) return null;

  const set = (key: keyof ToolModel | string, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const liveValidate = (next: Partial<ToolModel>) => {
    if (!submitted) return;
    setErrors(validate(next));
  };

  const handleSubmit = () => {
    const errs = validate(form);
    setErrors(errs);
    setSubmitted(true);
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    void onSave(form);
  };

  const subtitle = editing
    ? `Tool models · Editing #${editing.id ?? ''}`
    : 'Tool models · New catalogue entry';
  const title = editing ? 'Edit tool model' : 'New tool model';
  const errorCount = Object.keys(errors).length;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tool-model-form-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(15, 23, 32, 0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px 5vh',
        overflowY: 'auto',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 620,
          background: 'var(--color-panel)',
          borderRadius: 14,
          boxShadow: '0 32px 80px rgba(0,0,0,0.30)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* ── Header ────────────────────────────────────────── */}
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '20px 24px 18px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 9,
                background: 'rgba(11,74,111,0.10)',
                color: 'var(--color-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              <Wrench size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.4,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </div>
              <h2
                id="tool-model-form-title"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 24,
                  fontWeight: 500,
                  letterSpacing: -0.4,
                  color: 'var(--color-text)',
                  marginTop: 3,
                  marginBottom: 4,
                  lineHeight: 1.2,
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--color-text-mid)',
                  margin: 0,
                  lineHeight: 1.45,
                  maxWidth: 480,
                }}
              >
                Catalogue entry. Fields marked with{' '}
                <span style={{ color: 'var(--color-bad)' }}>*</span> are required.
              </p>
            </div>
          </div>

          <button
            onClick={onCancel}
            aria-label="Close"
            disabled={saving}
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-dim)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <X size={18} />
          </button>
        </header>

        {/* ── Error summary ─────────────────────────────────── */}
        {submitted && errorCount > 0 && (
          <div
            role="alert"
            style={{
              padding: '10px 24px',
              background: 'rgba(185,28,28,0.06)',
              borderBottom: '1px solid rgba(185,28,28,0.15)',
              color: 'var(--color-bad)',
              fontSize: 12.5,
              fontFamily: 'var(--font-body)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <strong style={{ fontWeight: 600 }}>
              {errorCount} field{errorCount === 1 ? '' : 's'} need
              {errorCount === 1 ? 's' : ''} attention
            </strong>
            <span style={{ color: 'var(--color-text-mid)' }}>— please review the highlighted inputs.</span>
          </div>
        )}

        {/* ── Scrollable body ───────────────────────────────── */}
        <div
          style={{
            padding: '22px 24px 24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 26,
            background: 'var(--color-panel)',
          }}
        >
          {/* 01 Identity */}
          <FormSection number={1} title="Identity">
            <Field
              label="Name"
              required
              error={submitted ? errors.toolModelName : undefined}
            >
              <div data-field="toolModelName">
                <TextInput
                  value={form.toolModelName ?? ''}
                  placeholder="e.g. Perigon Acoustic Solid State 8 Sensor V3"
                  error={submitted && !!errors.toolModelName}
                  onChange={(e) => {
                    set('toolModelName', e.target.value);
                    liveValidate({ ...form, toolModelName: e.target.value });
                  }}
                />
              </div>
            </Field>

            <Field
              label="Model code"
              required
              error={submitted ? errors.model : undefined}
              hint="Short identifier used in URLs, exports and inventory lookups."
            >
              <div data-field="model">
                <TextInput
                  value={form.model ?? ''}
                  placeholder="GTI-PASS8-V3"
                  error={submitted && !!errors.model}
                  style={{ fontFamily: 'var(--font-mono)' }}
                  onChange={(e) => {
                    set('model', e.target.value);
                    liveValidate({ ...form, model: e.target.value });
                  }}
                />
              </div>
            </Field>
          </FormSection>

          {/* 02 Description */}
          <FormSection
            number={2}
            title="Description"
            description="One or two sentences describing the tool, its sensors, and what it's typically used for."
          >
            <Field
              label="Description"
              required
              error={submitted ? errors.description : undefined}
            >
              <div data-field="description">
                <TextareaInput
                  value={form.description ?? ''}
                  rows={4}
                  placeholder="Third-generation acoustic SS8 sensor. Increased downhole temperature rating to 175°C, improved telemetry resolution at high ROP."
                  error={submitted && !!errors.description}
                  onChange={(e) => {
                    set('description', e.target.value);
                    liveValidate({ ...form, description: e.target.value });
                  }}
                />
              </div>
            </Field>
          </FormSection>
        </div>

        {/* ── Sticky footer ─────────────────────────────────── */}
        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 24px',
            background: 'var(--color-panel-alt)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--color-text-dim)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: 0.6,
            }}
          >
            {editing
              ? `LAST UPDATE · ${formatTs(editing.lastModifiedDate ?? editing.createdDate)}`
              : 'NEW RECORD'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <GhostButton onClick={onCancel} disabled={saving}>
              Cancel
            </GhostButton>
            <PrimaryButton onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Save model'}
            </PrimaryButton>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function formatTs(d?: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d;
  }
}
